//
// Support hours in the visitor's own timezone — design D6.
//
// The desk clock is already on the page, server-rendered and true. This adds
// the second clock and the badge, and it adds them only when it has something
// to say:
//
//   · the local card is revealed only when the visitor's UTC offset differs
//     from the desk's, so an IST visitor sees one clock rather than two
//     identical ones
//   · the badge and the advice line render only once computed
//
// Everything here is arithmetic on the offsets in the dataset. Nothing is
// fetched and nothing is guessed — if the settings are absent the snippet does
// not render this script's container at all.
//
(() => {
  const pad = (n) => (n < 10 ? '0' : '') + n;

  // "10:00" → 600. Returns null on anything it cannot read, so a mistyped
  // setting switches the conversion off rather than printing NaN.
  const toMinutes = (hhmm) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
    if (!m) return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
  };

  const hhmm = (mins) => {
    const m = ((mins % 1440) + 1440) % 1440;
    return pad(Math.floor(m / 60)) + ':' + pad(m % 60);
  };

  const init = (root) => {
    const open = toMinutes(root.dataset.open);
    const close = toMinutes(root.dataset.close);
    const deskOffset = Number(root.dataset.offset);
    if (open === null || close === null || !Number.isFinite(deskOffset)) return;

    const raw = root.querySelector('[data-gp-hours-strings]');
    let t = {};
    try {
      t = JSON.parse(raw.textContent);
    } catch (e) {
      return;
    }

    const closedDays = String(root.dataset.closedDays || '')
      .split(',')
      .map((d) => Number(d.trim()))
      .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);

    const now = new Date();
    const localOffset = -now.getTimezoneOffset();
    const shift = localOffset - deskOffset;

    // Is the desk open right now? Computed in desk time, from the visitor's
    // clock, so it is correct wherever they are.
    const deskMinutes = ((now.getHours() * 60 + now.getMinutes() - shift) % 1440 + 1440) % 1440;
    // The desk's own weekday, which is not always the visitor's.
    const dayCarry = Math.floor((now.getHours() * 60 + now.getMinutes() - shift) / 1440);
    const deskDay = ((now.getDay() + dayCarry) % 7 + 7) % 7;
    const isOpen =
      closedDays.indexOf(deskDay) === -1 && deskMinutes >= open && deskMinutes < close;

    const badge = root.querySelector('[data-gp-hours-badge]');
    if (badge) {
      badge.textContent = isOpen ? t.open : t.closed;
      badge.classList.toggle('is-open', isOpen);
      badge.hidden = false;
    }

    // Nothing below this line is worth showing to a visitor already on the
    // desk's clock.
    if (shift === 0) return;

    const openLocal = open + shift;
    const closeLocal = close + shift;

    const win = root.querySelector('[data-gp-hours-window]');
    if (win) win.textContent = hhmm(openLocal) + ' – ' + hhmm(closeLocal);

    const zone = root.querySelector('[data-gp-hours-zone]');
    if (zone) {
      let label = '';
      try {
        label = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      } catch (e) {
        label = '';
      }
      // "America/New_York" → "New York". The region prefix is noise next to a
      // clock the visitor already recognises as theirs. An engine that will
      // not name the zone leaves the element hidden rather than printing an
      // offset nobody reads.
      if (label) {
        zone.textContent = ' · ' + label.split('/').pop().replace(/_/g, ' ');
        zone.hidden = false;
      }
    }

    // The window can land on the previous or the next day. Saying which is the
    // difference between a useful conversion and a confusing one.
    const roll = root.querySelector('[data-gp-hours-rollover]');
    if (roll) {
      const openDay = Math.floor(openLocal / 1440);
      const closeDay = Math.floor(closeLocal / 1440);
      if (openDay !== 0 || closeDay !== 0) {
        roll.textContent = ' · ' + (openDay < 0 ? t.prev : t.next);
      }
    }

    // How much of their working day do we actually overlap? Measured as
    // minutes shared with 09:00–18:00 local, not inferred from the opening
    // time — testing the opening time told a London buyer we were asleep
    // while they worked, when the desk is in fact open through five and a
    // half hours of their day.
    const advice = root.querySelector('[data-gp-hours-advice]');
    if (advice) {
      const DAY_START = 540; // 09:00
      const DAY_END = 1080; // 18:00
      let shared = 0;
      // The window can straddle midnight, so it is measured over the two days
      // it can land on rather than clamped into one.
      for (const offset of [-1440, 0, 1440]) {
        shared += Math.max(
          0,
          Math.min(closeLocal + offset, DAY_END) - Math.max(openLocal + offset, DAY_START)
        );
      }
      advice.textContent = shared >= 180 ? t.overlap : t.early;
      advice.hidden = false;
    }

    const local = root.querySelector('[data-gp-hours-local]');
    if (local) local.hidden = false;
  };

  document.querySelectorAll('[data-gp-hours]').forEach(init);
})();
